import { useCallback } from 'react';
import { isManagedInstance } from '@gonfalon/constants/constants';
import {
  isABTestingEnabled,
  isAuditLogEnabled,
  isCodeReferencesEnabled,
  isDisableCodeReferencesWithOverrideEnabled,
  isFlagLinksEnabled,
  isTeamsEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';

import { fetchFlag as fetchFlagAction } from '#actions/flags';
import { useWorkflowsFeatureEnablement } from '#context/workflows';
import { useDispatch } from '#hooks/useDispatch';
import { useSelector } from '#hooks/useSelector';
import { flagByKeySelector, flagStatusesByProjectKeySelector } from '#reducers/flags';
import { profileSelector } from '#reducers/profile';
import { currentEnvironmentEntitySelector } from '#reducers/projects';
import { isFullRep, ready } from '#utils/reduxUtils';

export function useManageFlagState({ projKey, envKey, flagKey }: { projKey: string; envKey: string; flagKey: string }) {
  const environment = useSelector(currentEnvironmentEntitySelector);

  const dispatch = useDispatch();

  const fetchFlag = useCallback(
    () =>
      dispatch(
        fetchFlagAction(projKey, flagKey, envKey, {
          expandEvaluation: true,
        }),
      ),
    [projKey, envKey, flagKey],
  );

  const flagFromState = useSelector((state) => flagByKeySelector(state, { flagKey }));

  const statuses = useSelector(flagStatusesByProjectKeySelector);

  const profile = useSelector(profileSelector);
  const status = flagFromState.entity ? statuses.getIn([envKey, 'entities', flagKey, 'entity']) : undefined;

  const breadcrumb = ready(flagFromState.request) ? flagFromState.entity?.name : null;

  const error = flagFromState.request && flagFromState.request.error;
  // if we get into the unexpected situation that we have a partial flag rep as the flag entity, this will result in a re-fetch
  const flag = flagFromState.request && isFullRep(flagFromState.request) ? flagFromState.entity : undefined;

  const reduxIsReady = ready(flagFromState.request) && flagFromState.request && isFullRep(flagFromState.request);
  const flagIsWellformed = flag?.environments.has(envKey);
  const isReady = reduxIsReady && flagIsWellformed;

  const { isWorkflowBuilderEnabled, isWorkflowBuilderUpsellEnabled } = useWorkflowsFeatureEnablement();

  return {
    isReady,
    error,
    projKey,
    envKey,
    envId: environment._id,
    flagKey,
    flag,
    status,
    breadcrumb,
    isWorkflowsTabEnabled: isWorkflowBuilderEnabled || isWorkflowBuilderUpsellEnabled,
    isABTestingEnabled: isABTestingEnabled(),
    isCodeReferencesEnabled: !isDisableCodeReferencesWithOverrideEnabled() && isCodeReferencesEnabled(),
    isFlagLinksEnabled: isFlagLinksEnabled(),
    isTeamsEnabled: isTeamsEnabled(),
    isAuditLogEnabled: isAuditLogEnabled(),
    isInAppDemoEnabled: !isManagedInstance(),
    fetchFlag,
    profile: profile.get('entity'),
  };
}
